import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import { CircleProgress, Content, Heading, Left, ProgressText, Right, Storage, Total } from './styles';
import UsageBox from './usagebox';
import { getWorkspaceUsageDetails } from '../../../../services/workspaceService';
import { isEmpty, mathRound } from '../../../../helpers/common';
import { captureException } from '../../../../services/logService';
import UsageLoadingState from '../../../../component/loading/usageLoadingState';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { Group, Header, PersonalText, Text, Container } from '../../styles';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

export interface WorkspaceUsageDetails {
  totalMembers: number;
  members: number;
  clients: number;
  projects: number;
  useStorage: number;
  totalStorage: number;
  sizeType: 'MB' | 'GB' | 'TB' | string; // adjust or narrow this as needed
}
function Usage() {
  // States
  const [loading, setLoading] = useState(false);
  const [workspaceStorage, setWorkspaceStorage] = useState<WorkspaceUsageDetails>();

  //mobx states
  const { currentWorkspace } = rootStore.workspaceStore || {};

  const {
    totalMembers = 0,
    members = 0,
    clients = 0,
    projects = 0,
    useStorage = 0,
    totalStorage = 0,
    sizeType = 'GB'
  } = workspaceStorage || {};
  // Other variables
  const isMobile = useMobileDevice();
  const percentage = (useStorage * 100) / totalStorage;
  const progressRadius = 60;
  const circumference = 2 * Math.PI * progressRadius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!isEmpty(currentWorkspace?.id)) {
        const response = await getWorkspaceUsageDetails();
        setWorkspaceStorage(response);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div>
      <AppLayout>
        <Container className='desktop-content'>
          {loading && !workspaceStorage?.members && <UsageLoadingState />}
          {workspaceStorage?.members && (
            <Group>
              {!isMobile ? (
                <Header>
                  <PersonalText>Usage</PersonalText>
                </Header>
              ) : (
                <ResponsiveHeader title='Usage' />
              )}
              <Content>
                <Storage>
                  <Left>
                    <CircleProgress viewBox='0 0 152 152' width={136} height={136}>
                      <circle cx='75' cy='75' r={progressRadius} className='progress-trail' />
                      <circle
                        cx='75'
                        cy='75'
                        r={progressRadius}
                        className='progress-bar'
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                      />
                    </CircleProgress>
                    <ProgressText x='50%' y='50%' dominantBaseline='middle' textAnchor='middle'>
                      {mathRound(useStorage, 2)} {sizeType}
                    </ProgressText>
                  </Left>
                  <Right>
                    <Heading>Storage Usage</Heading>
                    <Text>Teamcamp uses {mathRound(percentage)}% out of your free space.</Text>
                  </Right>
                </Storage>
                <Total>
                  <UsageBox
                    title={'Team Member'}
                    count={`${members} / ${totalMembers}`}
                    members={members}
                    totalMembers={totalMembers}
                    isTeamMember={true}
                  />
                  <UsageBox title={'Clients'} count={clients} />
                  <UsageBox title={'Projects'} count={projects} />
                </Total>
              </Content>
            </Group>
          )}
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </div>
  );
}

export default observer(Usage);
